export const MarketInsights = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Mid-Rise Market Insights
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding current market dynamics helps you make informed decisions about selling your mid-rise apartment building.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 text-center">
            <div className="text-5xl font-bold text-teal-600 mb-4">$8M-$25M</div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Typical Value Range</h3>
            <p className="text-gray-600 leading-relaxed">
              Most mid-rise properties trade in this range, depending on location, condition, and market.
            </p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 text-center">
            <div className="text-5xl font-bold text-teal-600 mb-4">5-7%</div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Cap Rate Range</h3>
            <p className="text-gray-600 leading-relaxed">
              Current cap rates for mid-rise properties vary by market and property quality.
            </p>
          </div>
          <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 text-center">
            <div className="text-5xl font-bold text-teal-600 mb-4">90-180</div>
            <h3 className="text-xl font-semibold mb-3 text-gray-900">Days on Market</h3>
            <p className="text-gray-600 leading-relaxed">
              Traditional sales take 3-6 months. We can close in 30-45 days.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Current Market Trends</h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <i className="ri-arrow-up-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Strong Investor Demand</h4>
                  <p className="text-gray-600">Institutional and private investors are actively seeking mid-rise properties in urban markets.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-arrow-up-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Urban Migration</h4>
                  <p className="text-gray-600">Continued demand for urban living is driving rent growth and property values in city centers.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-arrow-up-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Value-Add Opportunities</h4>
                  <p className="text-gray-600">Properties with below-market rents or deferred maintenance are particularly attractive to investors.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="ri-arrow-right-line text-teal-500 text-2xl mr-4 mt-1"></i>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-900">Financing Availability</h4>
                  <p className="text-gray-600">While interest rates have risen, financing remains available for quality mid-rise properties.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/f3baf4c936d5b34e728a840b6cd48284.jpg"
                alt="Mid-Rise Market"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">Why Now Is a Good Time to Sell</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Strong Buyer Pool:</strong> Multiple qualified buyers competing for quality properties</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Favorable Valuations:</strong> Property values remain strong despite market volatility</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Tax Planning:</strong> Strategic timing can optimize your tax situation</span>
                </li>
              </ul>
            </div>
            <div>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Avoid Future Costs:</strong> Rising insurance, taxes, and maintenance costs</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Liquidity Needs:</strong> Convert real estate equity to cash for other opportunities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl mr-3 mt-1"></i>
                  <span className="text-gray-700"><strong>Retirement Planning:</strong> Simplify your portfolio and reduce management burden</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};